/* Coffee Stage · Sharing Cup 旗艦系列頁
   ─────────────────────────────────────────────
   內容全部由 Liquid 寫進 HTML，JS 只加 class 與寫 CSS 變數。
   任何一段掛掉，內容仍然可讀、連結仍然可點。

   ⚠️ **下面那幾條敘事線的圖固定是暮山紫，不隨釉色變** —— 顏色一致
      才看得出五款的大小差別。§10 換的只有最上面那個購買區的圖，
      兩者不衝突：一個在比大小，一個在讓客人挑自己要買的那一隻。

   1 遮罩揭露｜2 剖面描繪｜3 進度軌｜4 分組帶橫滑｜5 檢視鏡
   6 Alpha／Beta 對比滑桿｜7 工序滾動點亮｜8 觸控切換｜9 影片 facade
   10 購買區就地換款換色｜11 購買區圖庫                              */

(() => {
  "use strict";

  /* 向 section 開頭那段 inline script 報到：這個旗標一掛上，它就不會在
     2.4 秒後把 .scs-js 拿掉。檔案沒載到＝旗標不存在＝進場遮罩整批失效，
     內容直接可見。放在最前面，任何後續錯誤都不影響這一句。 */
  window.__scsReady = true;

  const root = document.getElementById("sharing-cup-series");
  if (!root || root.dataset.enhanced === "true") return;
  root.dataset.enhanced = "true";

  /* --- 第四道保險 · 叫醒圖片（只叫視窗附近的）--------------------------
     實測：分頁在背景時 loading="lazy" 完全不觸發，24 張圖只有 1 張載入。
     前三道保險（.is-settled、rAF 探測、visibilitychange 掃描）救得了
     opacity 與 stroke-dashoffset，救不了圖片 —— 圖片的載入時機不歸 CSS 管。
     把 lazy 改成 eager 會讓瀏覽器立刻排隊下載，不必等到被繪製。

     ⚠️ 2026-08-13 收窄範圍。原本這裡是整頁 querySelectorAll 全部改 eager，
     而它掛在 visibilitychange 上 —— 客人在手機切一下 App 再回來，
     45 張圖會同時排隊下載與解碼。實測 414：切走再回來，lazy 47→0。
     背景分頁不載入圖片是真 bug，要修；但要修的是「看得到的那幾張」，
     不是整頁。改成只叫醒視窗上下一個半螢幕之內的，其餘留給瀏覽器原生
     lazy —— 客人捲到才載，本來就是對的行為。 */
  const WAKE_AHEAD = 1.5; /* 視窗高度的倍數，往下預留 */
  const WAKE_BEHIND = 0.5; /* 往上預留，回捲時不會空白 */
  const wakeImages = (scope) => {
    const host = scope || root;
    const vh = window.innerHeight || 800;
    const vw = window.innerWidth || 400;
    const lo = -vh * WAKE_BEHIND;
    const hi = vh * (1 + WAKE_AHEAD);
    /* 橫向也要擋：分組帶是橫滑的，同一排圖垂直位置全部一樣，
       只看上下的話一整排會被當成「在視窗附近」一次全叫醒。
       客人還沒滑到的那幾張留給原生 lazy。 */
    const xlo = -vw * WAKE_BEHIND;
    const xhi = vw * (1 + WAKE_BEHIND);
    let n = 0;
    Array.prototype.forEach.call(host.querySelectorAll('img[loading="lazy"]'), (img) => {
      const box = img.getBoundingClientRect();
      if (box.bottom < lo || box.top > hi) return;
      if (box.right < xlo || box.left > xhi) return;
      img.loading = "eager";
      n += 1;
    });
    return n;
  };

  /* --- 讓位給主題那條 mobile dock -------------------------------------
     .scs-selbar 與主題的 .mobile-dock 都停在 bottom:0，我們 z-index 90、
     它 20，所以我們蓋住它。實測 375 與 414 都是蓋住 68px 中的 59px，
     dock 的購物車按不到。往上讓，不是把 z-index 加高。

     主題自己的 --mobile-dock-height 不能用：它的值就是 "0px"
     （CSS 延後載入 ＋ defer 的 JS，connectedCallback 量到 0 就寫死），
     而 var() 的 fallback 只在變數沒設時頂上。所以自己量。

     量的是 dock 實際露出來多少（innerHeight − top），不是它的高度：
     dock 平常用 transform 收在畫面外，捲過 header 才滑上來，
     這樣寫就會跟著它進出，不必去猜它的狀態。
     r.height 為 0（桌面隱藏或還沒 upgrade）時一律回 0。 */
  const dockGap = () => {
    const dock = document.querySelector(".mobile-dock");
    let gap = 0;
    if (dock) {
      const box = dock.getBoundingClientRect();
      if (box.height > 0) {
        gap = Math.min(Math.max(0, window.innerHeight - box.top), box.height);
      }
    }
    document.documentElement.style.setProperty("--cs-dock-h", Math.round(gap) + "px");
  };
  let gapTick = 0;
  const queueGap = () => {
    if (gapTick) return;
    gapTick = window.requestAnimationFrame(() => {
      gapTick = 0;
      dockGap();
    });
  };
  dockGap();
  /* dock 的 CSS 是 media="print" onload 延後載入的，第一次量可能還沒有樣式 */
  [200, 800, 1600].forEach((t) => window.setTimeout(dockGap, t));
  window.addEventListener("scroll", queueGap, { passive: true });
  window.addEventListener("resize", queueGap);
  document.addEventListener("transitionend", (e) => {
    if (e.target && e.target.classList && e.target.classList.contains("mobile-dock")) dockGap();
  });

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const qsa = (sel, el) => Array.prototype.slice.call((el || root).querySelectorAll(sel));
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const inView = (el) => {
    const box = el.getBoundingClientRect();
    return box.top < window.innerHeight * 0.92 && box.bottom > 0;
  };

  /* 背景分頁會凍結動畫時間軸，transition 停在起點＝內容消失。
     計時器不受影響，1700ms 後補 .is-settled 鎖死終態。
     1700 不是隨手取的：剖面描繪 1400ms ＋ 最深一階 221ms 的 delay ＝ 1621ms，
     鎖得比它早就會把線稿砍在半路。 */
  const settle = (el, cls) => {
    el.classList.add(cls || "is-in");
    /* ⚠️ 2026-08-15 由 1700 → 2200ms。店主回報「線稿會卡在顯示一半，
       等半秒之後才自己畫完」。成因是這個計時器把動畫切斷：
       描繪是 1400ms，而五個杯子的 --d 交錯最大到 221ms，
       最後一條要 1621ms 才畫完 —— 距離 1700ms 只剩 79ms。
       任何一格掉幀（進場時同時在跑遮罩、圖片解碼、字體換手）
       就會在動畫沒跑完時被 .is-settled 的 animation:none 攔腰砍斷，
       畫面上就是「停在一半，然後跳到完成」。
       液面升起（900ms ＋ 同一組 --d）也在這個窗口內，一併有餘裕。 */
    window.setTimeout(() => el.classList.add("is-settled"), 2200);
  };

  /* --------------------------------------- 1 · 進場遮罩揭露 ---- */
  const reveals = qsa(".scs-reveal");
  const showAll = () => {
    reveals.forEach((el) => el.classList.add("is-in", "is-settled"));
    wakeImages();
  };

  if (reduce || !("IntersectionObserver" in window)) {
    showAll();
  } else {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          settle(e.target);
          io.unobserve(e.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.02 }
    );
    reveals.forEach((el) => io.observe(el));

    /* IntersectionObserver 與 scroll 事件都掛在「更新畫面」那一步上：
       頁面沒有被繪製時（背景分頁、部分內嵌瀏覽器、擷圖服務）兩者都不會
       發生，內容就會整段停在 opacity:0 —— 綁捲動的保險同樣救不了。
       用一格 rAF 探測有沒有在繪製，沒有就整批揭開。 */
    let painted = false;
    requestAnimationFrame(() => {
      painted = true;
    });
    window.setTimeout(() => {
      if (!painted) showAll();
    }, 1600);
  }

  /* ------------------------ 2 · 剖面線稿描繪（--len 量測）------- */
  /* CSS 不知道路徑多長。量不到就維持完整線稿，不會變空白。 */
  const paths = qsa(".scs-path");
  paths.forEach((p) => {
    let len = 0;
    try {
      len = p.getTotalLength();
    } catch (err) {
      return;
    }
    if (len > 0) p.style.setProperty("--len", len.toFixed(1));
  });

  /* 隱藏線稿是一個要爭取的狀態，不是預設狀態。
     CSS 只在 data-scs-draw="on" 時才把 dashoffset 推到全長 —— 這個屬性
     由一格 rAF 掛上，而 rAF 只有在頁面真的在繪製時才回呼。
     背景分頁、bfcache 還原、in-app webview、Theme Editor 的 iframe 一律
     收不到這一格，於是屬性不存在，線稿就是一開始畫好的完整圖。 */
  if (!reduce) {
    requestAnimationFrame(() => {
      root.dataset.scsDraw = "on";
    });
  }

  /* 動畫跑到一半被切到背景，時間軸凍結在中途 —— 回來的時候它不會自己補完。
     切回可見時掃一次：還有非零 dashoffset、又沒有任何動畫在跑的，直接歸零。
     還沒進場的（祖先沒有 .is-in）不動，那些線本來就等著被畫。 */
  const sweepPaths = () => {
    paths.forEach((p) => {
      const host = p.closest(".scs-reveal");
      if (host && !host.classList.contains("is-in")) return;
      const off = parseFloat(getComputedStyle(p).strokeDashoffset);
      if (!off) return;
      const running =
        typeof p.getAnimations === "function" &&
        p.getAnimations().some((a) => a.playState === "running");
      if (running) return;
      p.style.strokeDasharray = "none";
      p.style.strokeDashoffset = "0";
    });
  };
  document.addEventListener("visibilitychange", () => {
    /* 兩個方向都叫醒圖片：切走的那一刻 lazy 就不再觸發，等切回來才補
       等於讓客人盯著空框。切回來時再掃一次，補上切走期間錯過的。
       只叫視窗附近的那幾張（見 wakeImages）—— 客人切個 App 回來，
       不該換來 45 張圖同時下載。 */
    wakeImages();
    if (document.hidden) return;
    window.setTimeout(sweepPaths, 120);
  });

  /* --------------------------------------- 3 · 頂部進度軌 ------ */
  /* 連結是純錨點，沒有 JS 一樣跳得到。這裡標出所在的一幕、把進度畫成
     1px 的線，並把軌推到主題 header 之下 —— header 是 sticky z-index:100，
     兩條都停在 top:0 就只看得到它。 */
  const rail = root.querySelector("[data-scs-rail]");
  if (rail) {
    const links = qsa("[data-scs-rail-link]", rail);
    const acts = links
      .map((a) => document.getElementById(a.getAttribute("data-scs-rail-link")))
      .filter(Boolean);
    const fill = root.querySelector("[data-scs-fill]");
    const heads = qsa(".shopify-section-group-header-group", document);
    let lastTop = -1;
    let ticking = false;

    const paint = () => {
      ticking = false;

      let top = 0;
      heads.forEach((el) => {
        const s = getComputedStyle(el).position;
        if (s !== "sticky" && s !== "fixed") return;
        const r = el.getBoundingClientRect();
        if (r.top <= 2 && r.height > 8) top = Math.max(top, r.bottom);
      });
      top = Math.round(top);
      if (top !== lastTop) {
        lastTop = top;
        root.style.setProperty("--scs-rail-top", top + "px");
      }

      const edge = rail.getBoundingClientRect().bottom + 4;
      let active = -1;
      acts.forEach((sec, i) => {
        if (sec.getBoundingClientRect().top <= edge) active = i;
      });
      links.forEach((a, i) => a.classList.toggle("is-on", i === active));

      const box = root.getBoundingClientRect();
      const span = box.height - window.innerHeight;
      const pct = span > 0 ? Math.max(0, Math.min(100, (-box.top / span) * 100)) : 0;
      if (fill) fill.style.setProperty("--scs-progress", pct.toFixed(1) + "%");
    };
    const tick = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    };

    window.addEventListener("scroll", tick, { passive: true });
    window.addEventListener("resize", tick, { passive: true });
    paint();
  }

  /* --------------------------------------- 4 · 分組帶橫滑 ------ */
  /* 橫滑本身是 CSS。這裡只擋掉邊界把整頁帶走，並把 focus 到的卡片捲進來。 */
  qsa("[data-scs-band]").forEach((band) => {
    const over = () => band.scrollWidth > band.clientWidth;

    band.addEventListener(
      "wheel",
      (e) => {
        if (!over() || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        const s = band.scrollLeft;
        if ((s <= 0 && e.deltaX < 0) || (s + band.clientWidth >= band.scrollWidth - 1 && e.deltaX > 0)) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    qsa("a", band).forEach((a) => {
      a.addEventListener("focus", () => {
        const card = a.closest(".scs-card");
        if (!card || !over()) return;
        const cb = card.getBoundingClientRect();
        const bb = band.getBoundingClientRect();
        /* 減掉那 20px 的起始內距 —— 跟 CSS 的 scroll-padding-inline-start
           是同一件事。少了它，鍵盤 focus 會把卡片一樣貼上螢幕左邊，
           跟手指滑動時的「跑框」完全同一個 bug。 */
        const pad = parseFloat(getComputedStyle(band).scrollPaddingInlineStart) || 0;
        if (cb.left < bb.left + pad || cb.right > bb.right) {
          band.scrollLeft += cb.left - bb.left - pad;
        }
      });
    });
  });

  /* --------------------------------------- 5 · 檢視鏡 ---------- */
  /* 放大原點跟著指標走，滑到哪就湊近看哪 ——「拿起來對著光看」的手勢。

     以前這一整段關在 (hover: hover) and (pointer: fine) 裡，而 CSS 的
     縮放也關在同一個 media query 裡：手機上點下去，class 沒換、transform
     仍然是 none —— 兩個 261×261 的區域整個是死的。
     現在觸控走 click 分支，跟型號頁 cs-sc-model.js §3 同一套：
     點一下推近、再點一下收回，同時只開一個，捲出視窗自動收起。 */
  const loupes = qsa("[data-scs-loupe]");
  const setOrigin = (frame, event) => {
    const box = frame.getBoundingClientRect();
    if (!box.width || !box.height) return false;
    const x = clamp(((event.clientX - box.left) / box.width) * 100, 0, 100);
    const y = clamp(((event.clientY - box.top) / box.height) * 100, 0, 100);
    frame.style.setProperty("--scs-loupe-x", x.toFixed(1) + "%");
    frame.style.setProperty("--scs-loupe-y", y.toFixed(1) + "%");
    return true;
  };
  const clearOrigin = (frame) => {
    frame.style.removeProperty("--scs-loupe-x");
    frame.style.removeProperty("--scs-loupe-y");
  };

  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    loupes.forEach((frame) => {
      frame.addEventListener("pointermove", (e) => setOrigin(frame, e), { passive: true });
      frame.addEventListener("pointerleave", () => clearOrigin(frame));
    });
  } else {
    let openLoupe = null;
    const closeLoupe = () => {
      if (!openLoupe) return;
      openLoupe.classList.remove("is-on");
      clearOrigin(openLoupe);
      openLoupe = null;
    };
    loupes.forEach((frame) => {
      frame.addEventListener("click", (event) => {
        if (frame === openLoupe) {
          closeLoupe();
          return;
        }
        closeLoupe();
        if (!setOrigin(frame, event)) return;
        frame.classList.add("is-on");
        openLoupe = frame;
      });
    });
    if ("IntersectionObserver" in window && loupes.length) {
      const lio = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting && e.target === openLoupe) closeLoupe();
          });
        },
        { threshold: 0.35 }
      );
      loupes.forEach((f) => lio.observe(f));
    }
  }

  /* ------------------------------ 6 · Alpha／Beta 對比滑桿 ------ */
  /* 兩張圖與各自的 alt 一直在 DOM 裡，上層只用 clip-path 收邊。
     沒有 JS 時停在 38%，Alpha 的凹入杯口與 Beta 的中央柱各看得到一部分。 */
  qsa("[data-scs-compare]").forEach((stage) => {
    const range = stage.parentElement.querySelector("[data-scs-compare-range]");
    const set = (pct) => {
      const v = clamp(pct, 0, 100);
      stage.style.setProperty("--scs-pos", v + "%");
      if (range && Number(range.value) !== Math.round(v)) range.value = String(Math.round(v));
    };

    const fromEvent = (e) => {
      const box = stage.getBoundingClientRect();
      if (!box.width) return;
      set(((e.clientX - box.left) / box.width) * 100);
    };

    let dragging = false;
    stage.addEventListener("pointerdown", (e) => {
      /* 擋掉原生的圖片拖曳。CSS 那邊已經用 user-drag: none ＋
         pointer-events: none 把 <img> 排除，這一行是第二道 ——
         Safari 對 user-drag 的支援不一致，而一旦原生 drag 起手，
         後面的 pointermove 就全部收不到。 */
      e.preventDefault();
      dragging = true;
      if (stage.setPointerCapture) stage.setPointerCapture(e.pointerId);
      fromEvent(e);
    });
    /* 桌面瀏覽器還會從 dragstart 這條路啟動，一併擋住 */
    stage.addEventListener("dragstart", (e) => e.preventDefault());
    stage.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      e.preventDefault();
      fromEvent(e);
    });
    const stop = () => {
      dragging = false;
    };
    stage.addEventListener("pointerup", stop);
    stage.addEventListener("pointercancel", stop);
    window.addEventListener("pointerup", stop);

    if (range) {
      range.addEventListener("input", () => set(Number(range.value)));
      set(Number(range.value));
    }
  });

  /* --------------------------------- 7 · 工序滾動點亮 ---------- */
  /* 十三道工序各自是一個 <li>，往下捲時依序點亮，左邊那條 1px 跟著長。
     未進場的只是 opacity 0.42 —— 不用 display:none、不用 visibility，
     爬蟲與關掉 JS 的人讀到的是同一份。
     deepest 是「捲過的最深一階」：不記住它的話，往回捲會讓刻度倒退，
     看起來像燒到一半又退火。 */
  (() => {
    const wrap = root.querySelector("[data-scs-proc]");
    if (!wrap) return;
    const stages = qsa("[data-scs-stage]", wrap);
    if (!stages.length) return;

    const paint = (index) => {
      const pct = ((index + 1) / stages.length) * 100;
      wrap.style.setProperty("--scs-proc", pct.toFixed(1) + "%");
    };

    const lightAll = () => {
      stages.forEach((el) => el.classList.add("is-on", "is-settled"));
      wrap.style.setProperty("--scs-proc", "100%");
      wakeImages();
    };

    if (reduce || !("IntersectionObserver" in window)) {
      lightAll();
      return;
    }

    let deepest = -1;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          settle(e.target, "is-on");
          const i = stages.indexOf(e.target);
          if (i > deepest) {
            deepest = i;
            paint(i);
          }
        });
      },
      { rootMargin: "0px 0px -18% 0px", threshold: 0.15 }
    );
    stages.forEach((el) => io.observe(el));

    /* IntersectionObserver 在頁面沒有被繪製時完全不會回呼（背景分頁、
       部分內嵌瀏覽器）。這個掃描器跑同一套判斷，全部就位後自行解除。 */
    let pending = true;
    const sweep = () => {
      if (!pending) return;
      let left = 0;
      stages.forEach((el, i) => {
        if (el.classList.contains("is-on")) return;
        if (inView(el)) {
          settle(el, "is-on");
          if (i > deepest) {
            deepest = i;
            paint(i);
          }
        } else {
          left += 1;
        }
      });
      if (left === 0) {
        pending = false;
        window.removeEventListener("scroll", sweep);
      }
    };
    window.addEventListener("scroll", sweep, { passive: true });
    window.setTimeout(sweep, 2000);

    /* sweep 只救得了「已經在視窗裡」的那幾階，而且它掛在 scroll 上 ——
       頁面沒有被繪製時 scroll 與 IntersectionObserver 都不會發生，
       十三道工序就會整段停在 opacity 0.42。那是正文，不是進場裝飾。
       跟第 1 段同一格 rAF 探測：探不到繪製就整批點亮。 */
    let painted = false;
    requestAnimationFrame(() => {
      painted = true;
    });
    window.setTimeout(() => {
      if (!painted) lightAll();
    }, 1600);
  })();

  /* --------------------------- 8 · 容量與釉面的觸控切換 -------- */
  /* 完整狀態由 CSS 的 :hover / :focus-visible 負責，
     這裡只補上觸控裝置需要的「點一下」，以及 SVG 那五格的鍵盤操作。

     五個杯型以前是 <g aria-hidden="true"> 掛 click：滑鼠點得到，
     Tab 到不了，而 aria-pressed 寫在一個對輔助技術不存在的節點上等於沒寫。
     現在它們是 role="button" tabindex="0"，名稱取自下方那一排本來就看得見的
     容量標籤（aria-labelledby），沒有新增任何一句文案。
     <g> 不是原生按鈕，Enter 與 Space 要自己補；Space 還要擋掉捲頁。 */
  const initToggles = (selector) => {
    const cells = qsa(selector);
    const toggle = (cell) => {
      const on = cell.classList.contains("is-on");
      cells.forEach((el) => {
        el.classList.remove("is-on");
        if (el !== cell) el.setAttribute("aria-pressed", "false");
      });
      if (!on) cell.classList.add("is-on");
      cell.setAttribute("aria-pressed", on ? "false" : "true");
      /* 店主 2026-08-15：「兩者都要」—— 滾到自動播一次，點一下可以再看一次。
         先移除、強制重排、再加回去，動畫才會從頭跑；只留 class 不會重播。
         reduced-motion 的人不播（那一組 CSS 把 animation 關掉）。 */
      if (!on) {
        cell.classList.remove("is-replay");
        void cell.getBoundingClientRect();
        cell.classList.add("is-replay");
      }
    };
    cells.forEach((cell) => {
      cell.addEventListener("click", () => toggle(cell));
      if (cell.tagName.toLowerCase() === "button") return;
      cell.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
        e.preventDefault();
        toggle(cell);
      });
    });
  };
  initToggles("[data-scs-cap-cell]");
  initToggles("[data-scs-gz-cell]");

  /* §8b（第一段時間線的捲動控制器）已整段移除：2026-08-16 改成三章式
     敘事索引，沒有互動也沒有捲動驅動 —— 純排版，不需要 JS。 */

  /* ------------------------------ 9 · 影片 facade -------------------- */
  /* 在按下去之前，這一段對 YouTube 是完全靜默的：沒有 iframe、沒有 script、
     沒有 cookie，連縮圖都是我們自己的 asset。按下去才注入 iframe，
     並且用 youtube-nocookie。autoplay=1 只在使用者主動點擊之後才出現 ——
     頁面自己不會播。
     沒有 JS 的話按鈕不做事，但封面、標題與 aria-label 都還在 DOM 裡，
     讀得到「這裡有一條講甚麼的影片」。 */
  qsa("[data-scs-vid]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      const id = btn.getAttribute("data-scs-vid");
      /* 攔不下來就讓連結照原樣跳去 YouTube —— 壞掉的那一邊要是「換個地方
         播」，不是「按了沒有反應」。 */
      if (!id || !/^[\w-]{6,20}$/.test(id)) return;
      e.preventDefault();
      const title = btn.getAttribute("data-scs-vid-title") || "Coffee Stage 影片";
      const frame = document.createElement("div");
      frame.className = "scs-vid-frame";
      const f = document.createElement("iframe");
      f.src =
        "https://www.youtube-nocookie.com/embed/" +
        encodeURIComponent(id) +
        "?autoplay=1&rel=0";
      f.title = title;
      f.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture");
      f.setAttribute("allowfullscreen", "");
      f.setAttribute("loading", "lazy");
      frame.appendChild(f);
      btn.replaceWith(frame);
    });
  });


  /* --------------------- 11 · 購買區圖庫（大圖可以翻）------------------
     整套從 assets/cs-sc-model.js 的 bindShots 搬過來，連 fail-open 的
     邊界一起搬：橫捲是 CSS 的 overflow-x，縮圖列是真的 <a href="#scs-gal-n">，
     關掉 JavaScript 一樣滑得動、跳得到。JS 只做三件事 ——
     ① 攔掉會寫進瀏覽器歷史的那一跳（並用 block:'nearest' 避免整頁彈一下）
     ② 前一張／下一張 ＋「第幾張」
     ③ 高亮縮圖列裡對應的那一格

     🔴 換款換色時 §10 會把整個 .scs-buy-gal-wrap 換掉 —— 所以每次都要
     重新查節點、重新綁，不能把節點快取在外面。綁定用 dataset 旗標防重複。 */
  let galIo = null;
  const bindBuyGal = () => {
    if (galIo) {
      galIo.disconnect();
      galIo = null;
    }
    const rail = root.querySelector(".scs-buy-gal");
    const figs = qsa(".scs-buy-gal > figure");
    const nav = qsa(".scs-buy-gal-nav a[data-scs-gal-thumb]");
    if (!rail || !figs.length) return;

    /* is-on 只是顏色。只用鍵盤或讀屏器的人看不到顏色，所以同一個狀態
       要再用 aria-current 說一次 —— 兩者一定同進同出。 */
    const mark = (a, on) => {
      a.classList.toggle("is-on", on);
      if (on) a.setAttribute("aria-current", "true");
      else a.removeAttribute("aria-current");
    };

    nav.forEach((a, i) => {
      mark(a, i === 0);
      if (a.dataset.scsGalBound === "1") return;
      a.dataset.scsGalBound = "1";
      a.addEventListener("click", (event) => {
        const id = a.getAttribute("href") || "";
        const target = id.charAt(0) === "#" ? root.querySelector(id) : null;
        if (!target) return; /* 找不到就交回原生錨點，不要把人卡在原地 */
        event.preventDefault();
        target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", inline: "start", block: "nearest" });
      });
    });

    const io = root.querySelector("[data-scs-gal-io]");
    const prev = root.querySelector("[data-scs-gal-prev]");
    const next = root.querySelector("[data-scs-gal-next]");
    const idxEl = root.querySelector("[data-scs-gal-i]");
    const numEl = root.querySelector("[data-scs-gal-n]");
    let idx = 0;

    const go = (i) => {
      const fig = figs[Math.max(0, Math.min(i, figs.length - 1))];
      if (!fig) return;
      fig.scrollIntoView({ behavior: reduce ? "auto" : "smooth", inline: "start", block: "nearest" });
    };

    const sync = (i) => {
      idx = i;
      if (idxEl) idxEl.textContent = String(i + 1);
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= figs.length - 1;
    };

    if (prev && prev.dataset.scsGalBound !== "1") {
      prev.dataset.scsGalBound = "1";
      prev.addEventListener("click", () => go(idx - 1));
    }
    if (next && next.dataset.scsGalBound !== "1") {
      next.dataset.scsGalBound = "1";
      next.addEventListener("click", () => go(idx + 1));
    }
    if (numEl) numEl.textContent = String(figs.length);

    /* ⚠️ 沒有 IntersectionObserver 就不解除 hidden —— 按鈕能捲，但
       「現在第幾張」會停在 1，一個會說謊的計數比沒有計數更糟。 */
    if (!nav.length || !("IntersectionObserver" in window)) return;
    if (io && figs.length > 1) io.hidden = false;
    sync(0);

    /* 🔴 用 intersectionRatio 挑「露出最多的那一格」，不要用 isIntersecting
       當門檻：往回滑時兩格都還重疊著軌道，兩筆 entry 都會通過
       `isIntersecting`，誰後被處理誰贏，高亮會停在剛剛離開的那一格。 */
    const ratios = new WeakMap();
    galIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => ratios.set(e.target, e.intersectionRatio));
        let best = 0;
        let bi = -1;
        figs.forEach((f, i) => {
          const r = ratios.get(f) || 0;
          if (r > best) {
            best = r;
            bi = i;
          }
        });
        if (bi < 0) return;
        nav.forEach((a, k) => mark(a, k === bi));
        sync(bi);

        /* 縮圖列自己也是一個 overflow-x 的捲動容器 —— 被高亮的那一格
           可能落在它的可視範圍外。只動這一條的 scrollLeft，
           不用 scrollIntoView（那會捲整頁）。 */
        const cur = nav[bi];
        const wrap = cur && cur.closest ? cur.closest(".scs-buy-gal-nav") : null;
        if (!wrap) return;
        const over = wrap.scrollWidth - wrap.clientWidth;
        if (over <= 0) return;
        const left = cur.offsetLeft - (wrap.clientWidth - cur.offsetWidth) / 2;
        wrap.scrollLeft = Math.max(0, Math.min(left, over));
      },
      /* 門檻要密：ratio 只在跨門檻時更新，稀疏的門檻會讓 WeakMap 留著舊值，
         兩格在門檻之間變化時高亮會慢半拍。 */
      { root: rail, threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );
    figs.forEach((f) => galIo.observe(f));
  };
  bindBuyGal();

  /* ------------------------- 10 · 購買區就地換款換色 ------------------
     2026-08-23 新增。五格型號與四格釉色本來就是真連結，而且必須繼續是真
     連結：關掉 JavaScript 要選得到、每個組合要有自己可以分享的網址、
     瀏覽器返回鍵要回得去。所以這裡不改 DOM 結構、不換成 <button>，
     只攔掉「整頁重載」這一個副作用。

     換上來的 HTML 是伺服器算的，不是前端拼的：用 Shopify 的 section
     rendering（?section_id=）只取這一個 section，所以圖片、價格、容量口徑、
     售罄狀態全部跟整頁重載算出來的一模一樣，前端沒有第二套邏輯可以跟它
     不一致。⚠️ DOMParser 解析出來的文件不會下載圖片 —— 只有真的被插進
     畫面的那幾張才會發請求。

     任何一環缺席就整段不啟用，連結回到原生行為。
     （這一套是從 assets/cs-sc-model.js §6 搬過來的，連 fail-closed 的
     邊界一起搬 —— 那些邊界每一條都是踩過才寫的，不要簡化。） */
  (() => {
    const buyBox = root.querySelector("[data-scs-buy]");
    if (!buyBox) return;
    if (!window.fetch || !window.DOMParser || !window.history || !history.pushState) return;

    const host = root.closest ? root.closest(".shopify-section") : null;
    const sectionId = host && host.id ? host.id.replace(/^shopify-section-/, "") : "";
    if (!sectionId) return;

    /* 整塊換掉的五塊。表單刻意不換 —— 換掉會把客人剛填的數量清掉，
       而且 <form> 上可能掛著主題自己的東西。只改它裡面那個 variant id
       與按鈕的兩個狀態。 */
    const SWAP = [
      "[data-scs-buy-gal]",
      "[data-scs-buy-tiles]",
      "[data-scs-buy-sw]",
      "[data-scs-buy-price]",
      "[data-scs-buy-meta]",
      "[data-scs-buy-more]",
    ];

    const strip = (u) => {
      const url = new URL(u, location.href);
      url.hash = "";
      return url.href;
    };

    const cache = new Map();
    const load = (url) => {
      const key = strip(url);
      if (cache.has(key)) return cache.get(key);
      const api = new URL(key);
      api.searchParams.set("section_id", sectionId);
      const p = fetch(api.href, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then((r) => (r.ok ? r.text() : null))
        .catch(() => null)
        .then((html) => {
          if (!html) cache.delete(key); /* 失敗不留在快取裡，下一次重試 */
          return html;
        });
      cache.set(key, p);
      return p;
    };

    const apply = (html) => {
      let doc;
      try {
        doc = new DOMParser().parseFromString(html, "text/html");
      } catch (err) {
        return false;
      }
      if (!doc || !doc.querySelector("[data-scs-buy]")) return false;

      /* 🔴 fail closed：先全部找齊，才動第一個節點。
         局部換掉會留下「圖換了、價格沒換」這種狀態在畫面上，而下面的
         variant id 照樣更新 —— 客人看到新款配舊價，按下去買到的是新款。
         任何一格缺席就整段放棄，go() 會接手做整頁重載。 */
      const pairs = [];
      for (let i = 0; i < SWAP.length; i += 1) {
        const next = doc.querySelector(SWAP[i]);
        const cur = root.querySelector(SWAP[i]);
        if (!next || !cur) return false;
        pairs.push([cur, next]);
      }

      /* 表單：只搬 variant id 與按鈕狀態，不動 <form> 本身。
         這一頁有表單而對面沒有 → 畫面換了、買到的是舊的，一樣整頁重載。 */
      const idInput = root.querySelector("#scs-form input[name='id']");
      const nextId = doc.querySelector("#scs-form input[name='id']");
      if (idInput && !nextId) return false;

      const btn = root.querySelector("#scs-form button[name='add']");
      const nextBtn = doc.querySelector("#scs-form button[name='add']");
      if (btn && !nextBtn) return false;

      /* 🔴 焦點承接：被換掉的節點若正握著焦點，replaceWith 之後焦點會掉回
         <body> —— 鍵盤使用者每換一款就被丟回頁首。 */
      const active = document.activeElement;
      const hadFocus = pairs.some((pair) => pair[0].contains(active));

      pairs.forEach((pair) => {
        /* importNode 而不是直接搬：跨 document 的節點在部分引擎上會出事 */
        pair[0].replaceWith(document.importNode(pair[1], true));
      });

      if (hadFocus) {
        const back =
          root.querySelector(".scs-buy-tile.is-on a") ||
          root.querySelector(".scs-buy-cell.is-on a") ||
          root.querySelector("[data-scs-buy-tiles]");
        if (back && back.focus) back.focus({ preventScroll: true });
      }

      if (idInput && nextId) idInput.value = nextId.value;

      /* 🔴 只寫進 <span>，不要動按鈕本身的 textContent。那個 <span> 是
         <product-form> 的契約：constructor 抓 submitButton.querySelector('span')
         存成 submitButtonText，用 textContent 賦值會把整個 span 換成一個
         文字節點，product-form 手上的參照就指向一棵離開了 DOM 的樹。 */
      if (btn && nextBtn) {
        btn.disabled = nextBtn.disabled;
        const lab = btn.querySelector("span");
        const nextLab = nextBtn.querySelector("span");
        if (lab && nextLab) lab.textContent = nextLab.textContent;
        else btn.textContent = nextBtn.textContent;
      }

      /* 圖庫整塊被換掉了 —— 節點全新，箭頭鍵與縮圖的監聽要重綁一次，
         不然換完款之後圖庫就變成一條只能用手指滑的軌。 */
      bindBuyGal();
      wakeImages(buyBox);
      return true;
    };

    let current = strip(location.href);
    let seq = 0;

    /* 🔴 換款期間必須擋住「加入購物車」。
       點下去的那一刻畫面就換了（樂觀更新，不必等網路），但表單裡的
       <input name="id"> 要等 section 抓回來、apply() 寫進去才是新的。
       中間那段時間按下去，送出的是**上一款**的 variant id。

       用 aria-disabled ＋ submit 攔截，不動按鈕的 disabled：
       apply() 會依伺服器版本設 disabled（那可能是售罄），那是它的職責。 */
    const busy = (on) => {
      const box = root.querySelector("[data-scs-buy]");
      if (box) {
        if (on) box.setAttribute("aria-busy", "true");
        else box.removeAttribute("aria-busy");
      }
      const form = root.querySelector("#scs-form");
      if (!form) return;
      if (on) form.setAttribute("data-scs-pending", "");
      else form.removeAttribute("data-scs-pending");
      const add = form.querySelector("button[name='add']");
      if (add) add.setAttribute("aria-disabled", on ? "true" : "false");
    };

    (() => {
      /* #scs-form 不在 SWAP 清單裡，節點不會被替換，所以監聽掛一次就夠。 */
      const form = root.querySelector("#scs-form");
      if (!form) return;
      form.addEventListener("submit", (e) => {
        if (form.hasAttribute("data-scs-pending")) e.preventDefault();
      });
    })();

    const go = (url, push) => {
      const key = strip(url);
      const mine = ++seq;
      busy(true);
      return load(key).then((html) => {
        if (mine !== seq) return; /* 連點兩格，只認最後一格 */
        if (!html || !apply(html)) {
          window.location.assign(key);
          return;
        }
        busy(false);
        if (push) history.pushState({ scsBuy: key }, "", key);
        current = key;
      });
    };

    const hit = (target) =>
      target && target.closest
        ? target.closest(".scs-buy-tile a[href], .scs-buy-cell a[href]")
        : null;

    /* 先抓起來放著：手指按下去到放開之間就開始下載。 */
    const warm = (e) => {
      const a = hit(e.target);
      if (a) load(a.href);
    };
    root.addEventListener("pointerdown", warm, { passive: true });
    root.addEventListener("pointerover", warm, { passive: true });

    root.addEventListener("click", (e) => {
      if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = hit(e.target);
      if (!a) return;
      let url;
      try {
        url = new URL(a.href, location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== location.origin) return;
      e.preventDefault();
      if (a.getAttribute("aria-current")) return; /* 已經是這一格 */

      /* 先把選中狀態搬過去，用的是那一格本來就印在畫面上的字 ——
         沒有新文案，也不必等網路。伺服器的版本回來時會覆蓋掉這一版。 */
      const cell = a.parentElement;
      const list = cell && cell.parentElement;
      if (list) {
        Array.prototype.forEach.call(list.children, (li) => {
          li.classList.toggle("is-on", li === cell);
          const link = li.querySelector("a");
          if (link && li !== cell) link.removeAttribute("aria-current");
        });
        a.setAttribute("aria-current", "true");
      }
      const pick = a.closest(".scs-buy-pick");
      const label = pick && pick.querySelector(".scs-buy-cur");
      const name = a.querySelector(".scs-buy-nm") || a.querySelector(".scs-buy-swnm");
      if (label && name) label.textContent = name.textContent;

      go(url.href, true);
    });

    /* 上一頁／下一頁也要走 go()，才有 busy() 的表單鎖與 seq 序列檢查。
       push 傳 false：瀏覽器已經幫我們改好網址了。 */
    window.addEventListener("popstate", () => {
      const next = strip(location.href);
      if (next === current) return; /* 只是錨點來回 */
      go(next, false);
    });
  })();
})();
